import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { copyToClipboard } from "../utils/copy_to_clipboard"
import { showHubTeamToast } from "../utils/hub_team_toast"

// The Academy Share control (TMS-840), in its two places:
//
// - On a Share menu (shared/_share_menu), beside the dropdown controller that opens
//   and places it: arrow keys between the items, Copy link with a visible "Link
//   copied" and a screen-reader announcement (the clipboard controller's status
//   pattern), and Share by email / SMS, which it hands to the page's dialog as a
//   share:compose event.
// - On the page's one share dialog (shared/_share_dialog): fills it for the item and
//   channel it was handed, posts it to Academy::SharesController, and shows the
//   server's per-field errors in place.
const CHANNELS = {
  email: {
    title: "Share by email",
    label: "Email addresses",
    type: "email",
    inputmode: "email",
    autocomplete: "email",
    placeholder: "name@example.com",
    hint: "Up to 5 addresses, separated by commas.",
    fineprint: "The Menopause Society sends this in your name, and replies come to your email address."
  },
  sms: {
    title: "Share by SMS",
    label: "Mobile numbers",
    type: "tel",
    inputmode: "tel",
    autocomplete: "tel",
    placeholder: "(555) 123-4567",
    hint: "Up to 5 numbers, separated by commas. Add the country code outside the US, e.g. +44.",
    fineprint: "The Menopause Society sends this in your name. Message and data rates may apply."
  }
}

export default class extends Controller {
  static targets = [
    "trigger", "status", "item",
    "form", "title", "kindField", "idField", "channelField", "previewType", "previewTitle", "baseError",
    "recipientsGroup", "recipientsLabel", "recipients", "recipientsHint", "noteGroup", "note", "noteHint",
    "fineprint", "submit"
  ]

  static values = {
    url: String,
    kind: String,
    id: String,
    label: String,
    typeLabel: String,
    endpoint: String,
    feedbackDuration: { type: Number, default: 2000 }
  }

  connect() {
    if (!this.hasFormTarget) return

    this.defaultNoteHint = this.noteHintTarget.textContent.trim()
    this.onShown = () => this.recipientsTarget.focus()
    // Back to the Share button that opened the dialog, so a keyboard user is not
    // dropped at the top of the page.
    this.onHidden = () => this.opener?.isConnected && this.opener.focus()
    this.element.addEventListener("shown.bs.modal", this.onShown)
    this.element.addEventListener("hidden.bs.modal", this.onHidden)
  }

  disconnect() {
    clearTimeout(this.feedbackTimeout)
    if (!this.hasFormTarget) return

    this.element.removeEventListener("shown.bs.modal", this.onShown)
    this.element.removeEventListener("hidden.bs.modal", this.onHidden)
  }

  // --- The menu ---------------------------------------------------------------

  // Runs after dropdown#toggle on the same click, so it only moves focus when that
  // click opened the menu.
  focusFirstItem() {
    requestAnimationFrame(() => {
      if (this.menuOpen) this.itemTargets[0]?.focus()
    })
  }

  navigate(event) {
    if (!this.hasItemTarget || !this.menuOpen) return

    const items = this.itemTargets
    const index = items.indexOf(document.activeElement)
    const next = {
      ArrowDown: items[(index + 1) % items.length],
      ArrowUp: items[(index - 1 + items.length) % items.length],
      Home: items[0],
      End: items[items.length - 1]
    }[event.key]
    if (!next) return

    event.preventDefault()
    next.focus()
  }

  // Tabbing out of an open menu closes it, as a native menu would.
  closeOnFocusOut(event) {
    if (event.relatedTarget && !this.element.contains(event.relatedTarget)) this.dropdown?.close()
  }

  async copy(event) {
    event.preventDefault()
    const copied = await copyToClipboard(this.urlValue)
    this.closeMenu()

    if (!copied) {
      this.announce("Couldn't copy the link")
      showHubTeamToast("Couldn't copy the link. Try again.", { variant: "error" })
      return
    }

    this.element.classList.add("is-copied")
    this.announce("Link copied")
    clearTimeout(this.feedbackTimeout)
    this.feedbackTimeout = setTimeout(() => {
      this.element.classList.remove("is-copied")
      this.announce("")
    }, this.feedbackDurationValue)
  }

  compose(event) {
    event.preventDefault()
    this.closeMenu()
    this.dispatch("compose", {
      target: window,
      detail: {
        channel: event.params.channel,
        kind: this.kindValue,
        id: this.idValue,
        label: this.labelValue,
        typeLabel: this.typeLabelValue,
        opener: this.triggerTarget
      }
    })
  }

  closeMenu() {
    this.dropdown?.close()
    if (this.hasTriggerTarget) this.triggerTarget.focus()
  }

  announce(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  get dropdown() {
    return this.application.getControllerForElementAndIdentifier(this.element, "dropdown")
  }

  get menuOpen() {
    return this.dropdown?.isOpen === true
  }

  // --- The dialog -------------------------------------------------------------

  open(event) {
    const { channel, kind, id, label, typeLabel, opener } = event.detail
    const config = CHANNELS[channel]
    if (!config) return

    this.opener = opener
    this.config = config
    this.formTarget.reset()
    this.clearErrors()

    this.kindFieldTarget.value = kind
    this.idFieldTarget.value = id
    this.channelFieldTarget.value = channel
    this.titleTarget.textContent = config.title
    this.previewTypeTarget.textContent = typeLabel
    this.previewTypeTarget.hidden = !typeLabel
    this.previewTitleTarget.textContent = label
    this.recipientsLabelTarget.textContent = config.label
    this.recipientsTarget.type = config.type
    this.recipientsTarget.multiple = config.type === "email"
    this.recipientsTarget.inputMode = config.inputmode
    this.recipientsTarget.autocomplete = config.autocomplete
    this.recipientsTarget.placeholder = config.placeholder
    this.fineprintTarget.textContent = config.fineprint

    Modal.getOrCreateInstance(this.element).show()
  }

  async submit(event) {
    event.preventDefault()
    this.clearErrors()
    this.submitTarget.disabled = true

    try {
      const response = await fetch(this.endpointValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
        body: new FormData(this.formTarget)
      })
      const data = await response.json().catch(() => ({}))

      if (response.ok) {
        Modal.getOrCreateInstance(this.element).hide()
        showHubTeamToast(data.message || "Sent.")
      } else {
        this.showErrors(data.errors || { base: "Something went wrong. Try again." })
      }
    } catch {
      this.showErrors({ base: "Couldn't reach the server. Check your connection and try again." })
    } finally {
      this.submitTarget.disabled = false
    }
  }

  showErrors(errors) {
    const base = errors.base || errors.channel
    if (base) {
      this.baseErrorTarget.textContent = base
      this.baseErrorTarget.hidden = false
    }
    if (errors.recipients) this.markField(this.recipientsGroupTarget, this.recipientsTarget, this.recipientsHintTarget, errors.recipients)
    if (errors.note) this.markField(this.noteGroupTarget, this.noteTarget, this.noteHintTarget, errors.note)

    const firstInvalid = [this.recipientsTarget, this.noteTarget].find((field) => field.getAttribute("aria-invalid"))
    firstInvalid?.focus()
  }

  markField(group, field, hint, message) {
    group.classList.add("has-error")
    field.setAttribute("aria-invalid", "true")
    hint.textContent = message
    hint.classList.add("is-error")
  }

  clearErrors() {
    this.baseErrorTarget.hidden = true
    this.baseErrorTarget.textContent = ""

    const fields = [
      [this.recipientsGroupTarget, this.recipientsTarget, this.recipientsHintTarget, this.config?.hint || ""],
      [this.noteGroupTarget, this.noteTarget, this.noteHintTarget, this.defaultNoteHint]
    ]
    fields.forEach(([group, field, hint, text]) => {
      group.classList.remove("has-error")
      field.removeAttribute("aria-invalid")
      hint.textContent = text
      hint.classList.remove("is-error")
    })
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
